import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, ArrowLeft, AlertCircle, CheckCircle2, User, Truck,
  Package, Info,
} from 'lucide-react';
import { api } from '@/utils/api';

// Service & Warranty module — NEW ticket creation
// Norman: the form maps directly to the ticket domain model. Each choice
// constrains the next dropdown, preventing invalid state. No silent defaults
// that the user can't see.

type TicketType = 'service';
type ServiceSubtype = 'after_sales' | 'warranty' | 'paid';
type ServiceCategory = 'installation' | 'service';
type AssignmentMode = 'agent' | 'vendor';

interface ProductUnitLite {
  id: string;
  code: string;
  productName?: string;
  serialNumber?: string;
}

interface UserLite {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

interface VendorLite {
  id: string;
  name: string;
  phone?: string;
  vendorType?: string;
  serviceCategories?: string[];
}

interface FormState {
  ticketType: TicketType;
  serviceSubtype: ServiceSubtype;
  serviceCategory: ServiceCategory;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  productUnitId: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  assignmentMode: AssignmentMode;
  assignedToId: string;
  vendorId: string;
}

const DEFAULT_FORM: FormState = {
  ticketType: 'service',
  serviceSubtype: 'warranty',
  serviceCategory: 'service',
  title: '',
  description: '',
  priority: 'medium',
  productUnitId: '',
  customerName: '',
  customerPhone: '',
  customerAddress: '',
  assignmentMode: 'agent',
  assignedToId: '',
  vendorId: '',
};

export function ServiceTicketCreatePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const [productUnits, setProductUnits] = useState<ProductUnitLite[]>([]);
  const [users, setUsers] = useState<UserLite[]>([]);
  const [vendors, setVendors] = useState<VendorLite[]>([]);
  const [productUnitQuery, setProductUnitQuery] = useState('');

  useEffect(() => {
    // Parallel loads — non-blocking so form appears instantly
    api.get('/users').then(r => {
      const data = r.data.data?.users ?? r.data.data ?? [];
      setUsers(Array.isArray(data) ? data : []);
    }).catch(() => setUsers([]));
    api.get('/vendors').then(r => {
      const data = r.data.data?.vendors ?? r.data.data ?? [];
      setVendors(Array.isArray(data) ? data : []);
    }).catch(() => setVendors([]));
  }, []);

  // Product unit search — debounce-lite
  useEffect(() => {
    if (!productUnitQuery || productUnitQuery.length < 2) {
      setProductUnits([]);
      return;
    }
    const timer = setTimeout(() => {
      api.get(`/warehouse/product-units?q=${encodeURIComponent(productUnitQuery)}&limit=20`)
        .then(r => {
          const data = r.data.data?.productUnits ?? r.data.data ?? [];
          setProductUnits(Array.isArray(data) ? data : []);
        })
        .catch(() => setProductUnits([]));
    }, 350);
    return () => clearTimeout(timer);
  }, [productUnitQuery]);

  const update = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setFieldErrors(prev => ({ ...prev, [key]: undefined }));
  }, []);

  const validate = useCallback((): boolean => {
    const errors: Partial<Record<keyof FormState, string>> = {};
    if (!form.title.trim()) errors.title = 'Title is required';
    if (!form.customerName.trim()) errors.customerName = 'Customer name required';
    if (!form.customerPhone.trim()) errors.customerPhone = 'Phone number required';
    else if (!/^[+\d\s-]{8,}$/.test(form.customerPhone.trim())) errors.customerPhone = 'Invalid phone';
    if (!form.customerAddress.trim()) errors.customerAddress = 'Address required';
    if (form.assignmentMode === 'agent' && !form.assignedToId) errors.assignedToId = 'Pick an agent';
    if (form.assignmentMode === 'vendor' && !form.vendorId) errors.vendorId = 'Pick a vendor';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [form]);

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    setSubmitError(null);
    try {
      const body: Record<string, unknown> = {
        ticketType: form.ticketType,
        serviceSubtype: form.serviceSubtype,
        serviceCategory: form.serviceCategory,
        title: form.title,
        description: form.description || undefined,
        priority: form.priority,
        customerName: form.customerName,
        customerPhone: form.customerPhone,
        customerAddress: form.customerAddress,
        productUnitId: form.productUnitId || undefined,
        assignmentMode: form.assignmentMode,
        assignedToId: form.assignmentMode === 'agent' ? form.assignedToId : undefined,
        vendorId: form.assignmentMode === 'vendor' ? form.vendorId : undefined,
        // Legacy mapping for existing backend route
        category: form.serviceCategory,
      };
      const res = await api.post('/service/tickets', body);
      const created = res.data.data?.ticket ?? res.data.data ?? res.data;
      if (created?.id) {
        navigate(`/service-tickets/${created.id}`);
      } else {
        navigate('/service-tickets');
      }
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Failed to create ticket';
      setSubmitError(msg);
    } finally {
      setSaving(false);
    }
  };

  const vendorsFiltered = vendors.filter(v =>
    !v.vendorType || v.vendorType === 'service' || v.vendorType === 'both',
  );

  return (
    <div className="mx-auto max-w-3xl">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate('/service-tickets')}
          className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          aria-label="Back to tickets"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create Service Ticket</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Complete the form. Fields marked with * are required.</p>
        </div>
      </div>

      {submitError && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
          <div>{submitError}</div>
        </div>
      )}

      <div className="space-y-6">
        {/* Classification */}
        <Section title="Classification" icon={<Info size={16} />}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Field label="Ticket Type *">
              <Pill
                value={form.ticketType}
                options={[{ value: 'service', label: 'Service' }]}
                onChange={v => update('ticketType', v as TicketType)}
              />
            </Field>
            <Field label="Subtype *">
              <select
                value={form.serviceSubtype}
                onChange={e => update('serviceSubtype', e.target.value as ServiceSubtype)}
                className={selectClass}
              >
                <option value="after_sales">After-sales (installation)</option>
                <option value="warranty">Warranty</option>
                <option value="paid">Paid service</option>
              </select>
            </Field>
            <Field label="Category *">
              <select
                value={form.serviceCategory}
                onChange={e => update('serviceCategory', e.target.value as ServiceCategory)}
                className={selectClass}
              >
                <option value="installation">Installation</option>
                <option value="service">Service / Repair</option>
              </select>
            </Field>
          </div>
          {form.serviceSubtype === 'after_sales' && form.serviceCategory !== 'installation' && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              After-sales tickets usually map to category <strong>Installation</strong>. Check your selection.
            </div>
          )}
        </Section>

        {/* Core info */}
        <Section title="Ticket Info" icon={<Info size={16} />}>
          <Field label="Title *" error={fieldErrors.title}>
            <input
              type="text"
              value={form.title}
              onChange={e => update('title', e.target.value)}
              placeholder="e.g. SS tank installation — customer name"
              className={inputClass(!!fieldErrors.title)}
            />
          </Field>
          <Field label="Description">
            <textarea
              rows={3}
              value={form.description}
              onChange={e => update('description', e.target.value)}
              placeholder="What the customer reported. Keep it factual."
              className={inputClass(false)}
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Priority">
              <select
                value={form.priority}
                onChange={e => update('priority', e.target.value as FormState['priority'])}
                className={selectClass}
              >
                <option value="low">Low (48h SLA)</option>
                <option value="medium">Medium (24h SLA)</option>
                <option value="high">High (8h SLA)</option>
                <option value="critical">Critical (4h SLA)</option>
              </select>
            </Field>
            <Field label="Linked Product Unit (barcode)">
              <input
                type="text"
                value={productUnitQuery}
                onChange={e => setProductUnitQuery(e.target.value)}
                placeholder="Search barcode or serial"
                className={inputClass(false)}
              />
              {productUnits.length > 0 && (
                <ul className="mt-1 max-h-36 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                  {productUnits.map(pu => (
                    <li key={pu.id}>
                      <button
                        type="button"
                        onClick={() => {
                          update('productUnitId', pu.id);
                          setProductUnitQuery(pu.code);
                          setProductUnits([]);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <Package size={14} className="text-gray-400" />
                        <span className="font-mono">{pu.code}</span>
                        <span className="text-xs text-gray-500">{pu.productName}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {form.productUnitId && (
                <p className="mt-1 flex items-center gap-1 text-xs text-green-700 dark:text-green-400">
                  <CheckCircle2 size={12} /> Unit linked
                </p>
              )}
            </Field>
          </div>
        </Section>

        {/* Customer */}
        <Section title="Customer" icon={<User size={16} />}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Customer Name *" error={fieldErrors.customerName}>
              <input
                type="text"
                value={form.customerName}
                onChange={e => update('customerName', e.target.value)}
                className={inputClass(!!fieldErrors.customerName)}
              />
            </Field>
            <Field label="Phone *" error={fieldErrors.customerPhone}>
              <input
                type="tel"
                value={form.customerPhone}
                onChange={e => update('customerPhone', e.target.value)}
                placeholder="+91 98XXX XXXXX"
                className={inputClass(!!fieldErrors.customerPhone)}
              />
            </Field>
          </div>
          <Field label="Address *" error={fieldErrors.customerAddress}>
            <textarea
              rows={2}
              value={form.customerAddress}
              onChange={e => update('customerAddress', e.target.value)}
              className={inputClass(!!fieldErrors.customerAddress)}
            />
          </Field>
        </Section>

        {/* Assignment */}
        <Section title="Assignment" icon={<UserIcon />}>
          <div className="mb-3 flex gap-2">
            <ModeButton
              active={form.assignmentMode === 'agent'}
              onClick={() => update('assignmentMode', 'agent')}
              icon={<User size={14} />}
              label="On-role agent"
              hint="GPS + photos mandatory"
            />
            <ModeButton
              active={form.assignmentMode === 'vendor'}
              onClick={() => update('assignmentMode', 'vendor')}
              icon={<Truck size={14} />}
              label="External vendor"
              hint="WhatsApp flow, no GPS"
            />
          </div>
          {form.assignmentMode === 'agent' ? (
            <Field label="Assign to agent *" error={fieldErrors.assignedToId}>
              <select
                value={form.assignedToId}
                onChange={e => update('assignedToId', e.target.value)}
                className={inputClass(!!fieldErrors.assignedToId)}
              >
                <option value="">Select agent</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <Field label="Assign to vendor *" error={fieldErrors.vendorId}>
              <select
                value={form.vendorId}
                onChange={e => update('vendorId', e.target.value)}
                className={inputClass(!!fieldErrors.vendorId)}
              >
                <option value="">Select vendor</option>
                {vendorsFiltered.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.name} {v.phone ? `— ${v.phone}` : ''}
                  </option>
                ))}
              </select>
              {vendorsFiltered.length === 0 && (
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                  No service-type vendors found. Add one in Vendors.
                </p>
              )}
            </Field>
          )}
        </Section>
      </div>

      {/* Actions */}
      <div className="mt-6 flex items-center justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
        <button
          onClick={() => navigate('/service-tickets')}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[var(--ink-1)] disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
          {saving ? 'Creating…' : 'Create Ticket'}
        </button>
      </div>
    </div>
  );
}

// ───────── Atoms ─────────

const inputClass = (err: boolean) =>
  `w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
    err
      ? 'border-red-400 focus:ring-red-200'
      : 'border-gray-300 focus:border-[var(--ink-0)] focus:ring-[var(--ink-3)] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100'
  }`;

const selectClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100';

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        <span className="text-gray-500 dark:text-gray-400">{icon}</span>
        {title}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}

function Pill({
  value,
  options,
  onChange,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex gap-2">
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
            value === o.value
              ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)] dark:bg-[var(--ink-0)] dark:text-[var(--saffron-600)]'
              : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  icon,
  label,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded-lg border p-3 text-left transition-all ${
        active
          ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] shadow-sm dark:bg-[var(--ink-0)]'
          : 'border-gray-300 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700'
      }`}
    >
      <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
        {icon}
        {label}
      </div>
      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{hint}</div>
    </button>
  );
}

function UserIcon() {
  return <User size={16} />;
}
